import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { CutoffOverview, CutoffWeek } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { fmtPacific, fmtUtcLocal } from '@/lib/cutoff'

/** Days after the Saturday a week ends, as the weekday they land on. */
const DAY_OPTIONS = [
  { value: 1, label: 'Sunday' },
  { value: 2, label: 'Monday' },
  { value: 3, label: 'Tuesday' },
  { value: 4, label: 'Wednesday' },
  { value: 5, label: 'Thursday' },
  { value: 6, label: 'Friday' },
  { value: 7, label: 'Saturday' },
]

function weekLabel(ending: string): string {
  const [y, m, d] = ending.split('-').map(Number)
  const start = new Date(y, m - 1, d - 6)
  const end = new Date(y, m - 1, d)
  const o: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }
  return `${start.toLocaleDateString(undefined, o)} – ${end.toLocaleDateString(undefined, o)}`
}

/** A datetime-local value 24 hours from now, in the viewer's own time. */
function tomorrowLocal(): string {
  const d = new Date(Date.now() + 24 * 3600 * 1000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/**
 * Pay setup → Supervisor cutoff.
 *
 * Asked for by Tim Brickell (2 Nov 2026) — a point after which supervisors
 * can no longer change a pay week, ahead of the export to Sage — with the
 * deadline adjustable here (Tom). The office is never held to it, and can
 * reopen a closed week for supervisors for a set time.
 *
 * Admin changes the deadline; Office and Admin reopen and close weeks.
 */
export function SupervisorCutoffPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'

  const [data, setData] = useState<CutoffOverview | null>(null)
  const [enabled, setEnabled] = useState(false)
  const [days, setDays] = useState(3)
  const [time, setTime] = useState('23:59')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [reopening, setReopening] = useState<string | null>(null)
  const [until, setUntil] = useState(tomorrowLocal())
  const [note, setNote] = useState('')

  const apply = useCallback((o: CutoffOverview) => {
    setData(o)
    setEnabled(o.setting.is_enabled)
    setDays(o.setting.days_after_week_end)
    setTime(o.setting.cutoff_time)
  }, [])

  useEffect(() => {
    api
      .cutoff()
      .then(apply)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load the cutoff'))
  }, [apply])

  const dirty =
    data !== null &&
    (enabled !== data.setting.is_enabled ||
      days !== data.setting.days_after_week_end ||
      time !== data.setting.cutoff_time)

  async function save() {
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      apply(await api.setCutoff({ is_enabled: enabled, days_after_week_end: days, cutoff_time: time }))
      setNotice(enabled ? 'Saved. The cutoff is on.' : 'Saved. The cutoff is off — supervisors can change any unpaid week.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  async function reopen(w: CutoffWeek) {
    setError(null)
    setNotice(null)
    try {
      apply(await api.reopenWeek(w.week_ending, new Date(until).toISOString(), note.trim() || null))
      setReopening(null)
      setNote('')
      setNotice(`Reopened ${weekLabel(w.week_ending)} for supervisors.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reopen the week')
    }
  }

  async function close(w: CutoffWeek) {
    setError(null)
    setNotice(null)
    try {
      apply(await api.closeWeek(w.week_ending))
      setNotice(`Closed ${weekLabel(w.week_ending)} to supervisors again.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not close the week')
    }
  }

  const dayName = DAY_OPTIONS.find((d) => d.value === days)?.label ?? ''
  const current = data?.weeks[0]

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      <div className="card">
        <h2 className="title" style={{ marginTop: 0 }}>Supervisor cutoff</h2>
        <p className="muted">
          When it is on, each pay week (Sunday to Saturday) closes to supervisors
          at the time set here. After that a supervisor cannot record, correct,
          move or remove time in that week, or tick or untick per diem — they are
          told to ask the office. The office can still change the week until it
          is exported or Sage pays it, and can reopen it for supervisors below.
        </p>

        {data === null ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: '0.75rem' }}>
              <input
                type="checkbox"
                checked={enabled}
                disabled={!isAdmin}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <strong>Close each week to supervisors</strong>
            </label>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label htmlFor="cut-day">On the</label>
                <select
                  id="cut-day"
                  value={days}
                  disabled={!isAdmin}
                  onChange={(e) => setDays(Number(e.target.value))}
                >
                  {DAY_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label} after the week ends
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label htmlFor="cut-time">At (Pacific)</label>
                <input
                  id="cut-time"
                  type="time"
                  value={time}
                  disabled={!isAdmin}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
              {isAdmin && (
                <button className="btn" disabled={!dirty || saving} onClick={() => void save()}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
            <p className="muted" style={{ marginBottom: 0 }}>
              {enabled
                ? `Weeks close to supervisors on the ${dayName} after they end, at ${time} Pacific.`
                : 'Off — supervisors can change any week Sage has not paid.'}
              {!isAdmin && ' Only an Admin can change the deadline.'}
              {data.setting.updated_by &&
                ` Last changed by ${data.setting.updated_by}${
                  data.setting.updated_utc ? `, ${fmtUtcLocal(data.setting.updated_utc)}` : ''
                }.`}
            </p>
            {dirty && current && (
              <p className="muted" style={{ marginBottom: 0 }}>
                Not saved yet. Moving the deadline applies to every week at once,
                including weeks already past.
              </p>
            )}
          </>
        )}
      </div>

      {data !== null && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Recent weeks</h2>
          {!data.setting.is_enabled && (
            <p className="muted">The cutoff is off, so every unpaid week is open to supervisors.</p>
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Pay week</th>
                  <th>Closes to supervisors</th>
                  <th>Now</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.weeks.map((w) => (
                  <tr key={w.week_ending}>
                    <td style={{ whiteSpace: 'nowrap' }}>{weekLabel(w.week_ending)}</td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {data.setting.is_enabled ? fmtPacific(w.closes_at) : '—'}
                    </td>
                    <td>
                      {!data.setting.is_enabled ? (
                        <span className="badge badge-ok">Open</span>
                      ) : w.reopened_until_utc ? (
                        <>
                          <span className="badge badge-warn">Reopened</span>{' '}
                          <span className="muted">
                            until {fmtUtcLocal(w.reopened_until_utc)}
                            {w.reopened_by && ` by ${w.reopened_by}`}
                            {w.reopen_note && ` — ${w.reopen_note}`}
                          </span>
                        </>
                      ) : w.closed ? (
                        <span className="badge badge-info">Closed</span>
                      ) : (
                        <span className="badge badge-ok">Open</span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {data.setting.is_enabled && w.reopened_until_utc && (
                        <button className="btn btn-secondary" onClick={() => void close(w)}>
                          Close now
                        </button>
                      )}
                      {data.setting.is_enabled && w.closed && reopening !== w.week_ending && (
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setReopening(w.week_ending)
                            setUntil(tomorrowLocal())
                            setNote('')
                          }}
                        >
                          Reopen…
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {reopening && (
            <div className="card" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
              <h3 style={{ marginTop: 0 }}>Reopen {weekLabel(reopening)} for supervisors</h3>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-field" style={{ marginBottom: 0 }}>
                  <label htmlFor="reopen-until">Until (your time)</label>
                  <input
                    id="reopen-until"
                    type="datetime-local"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                  />
                </div>
                <div className="form-field" style={{ marginBottom: 0, flex: '1 1 220px' }}>
                  <label htmlFor="reopen-note">Why (optional)</label>
                  <input
                    id="reopen-note"
                    maxLength={300}
                    placeholder="e.g. Sue's crew missed Friday travel"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                </div>
                <button
                  className="btn"
                  onClick={() => {
                    const w = data.weeks.find((x) => x.week_ending === reopening)
                    if (w) void reopen(w)
                  }}
                >
                  Reopen
                </button>
                <button className="btn btn-secondary" onClick={() => setReopening(null)}>
                  Cancel
                </button>
              </div>
              <p className="muted" style={{ marginBottom: 0 }}>
                Up to 14 days. It closes again by itself at that time.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
